import { useEffect } from 'react'
import { type Moment } from 'moment'
import { type SimpleOption } from '@retailer-platform/shared-components/src/common/select/utils/select.types'
import { useFilterParamsContext } from 'domains/insights-portal/utils/filter-params/filterParamsContext'
import {
  InsightsPortalSchema,
  useGetInsightsPortalFilterOptionsLazyQuery,
  useGetInsightsPortalMetricsQuery,
  useGetInsightsPortalGroupMetricsQuery,
  useGetInsightsPortalGroupComparisonQuery,
  type GetInsightsPortalGroupComparisonQueryVariables,
  InsightsPortalGroupComparisonRequestJoinBy,
  useGetInsightsPortalMultiGroupDataQuery,
  type GetInsightsPortalMultiGroupDataQueryVariables,
  useCreateInsightsPortalCustomReportMutation,
  GetInsightsPortalListCustomReportsDocument,
  InsightsPortalOwner,
} from 'domains/insights-portal/api'
import { type FilterParamsState } from 'domains/insights-portal/utils/filter-params/filterParams.types'
import { useFormatValue } from 'domains/insights-portal/utils/format-value.hooks'
import { OrderDeliveryMetrics } from 'domains/insights-portal/schemas/order-deliveries/insightsPortalOrderDeliveries.types'
import {
  type BaseDynamicFilterOptions,
  transformApiOptionsIntoDashboardOptions,
} from 'domains/insights-portal/schemas/utils/insightsPortalSchemas.utils'
import { useInsightsPortalAuthorizationContext } from 'domains/insights-portal/utils/authorization-context.hooks'
import { useDomainMessages } from 'domains/insights-portal/utils/domain/intl'
import { calculateComparisonPeriod } from 'domains/insights-portal/utils/insights-portal.utils'
import {
  transformFilterSelectionsToOrderDeliveriesSchemaFilters,
  transformOrderDeliveriesApiOptionsConfig,
} from 'domains/insights-portal/schemas/order-deliveries/insightsPortalOrderDeliveries.helpers'
import { useDataExplorerParamsContext } from 'domains/insights-portal/components/data-explorer/utils/filter-params/dataExplorerParamsContext'
import { useInsightsPortalQueryTags } from 'domains/insights-portal/utils/query-tags.hooks'
import { useSchema } from 'domains/insights-portal/utils/schema.hooks'

// Convert the api filter names to dashboard filter names
export const mapApiNameToDashboardName = {
  WAREHOUSE: 'retailer',
  WAREHOUSE_LOCATION: 'storeLocation',
  WAREHOUSE_LOCATION_CODE: 'storeLocationCode',
  PARTNER: 'partner',
  ORDER_SOURCE: 'orderSource',
  REGION: 'region',
  WAREHOUSE_LOCATION_STATE: 'state',
  FULFILLMENT_TYPE: 'fulfillmentType',
  ALCOHOL: 'alcohol',
  EXPRESS: 'express',
  VIRTUAL_WAREHOUSE: 'convenience',
  LOYALTY: 'loyalty',
  EBT: 'ebt',
  FIRST_ORDER: 'firstOrder',
  PARTNER_GEO_DIVISION: 'partnerDivision',
  ORDER_CURRENCY: 'orderCurrency',
  WAREHOUSE_LOCATION_GROUP: 'storeGroup',
  COUNT_ORDER: 'countOrder',
  AVG_GMV: 'avgGmv',
  SUM_GMV: 'sumGmv',
  COUNT_USER: 'countUser',
  AVG_RATING: 'avgRating',
  WEEK_SUN_PT: 'week',
  WEEK_MON_PT: 'week',
  WEEK_SAT_PT: 'week',
  MONTH_PT: 'month',
  COUNT_FIRST_ORDER: 'countFirstOrder',
  DATE_PT: 'date',
}

export const useOrderDeliveriesFilters = (filtersObjOverride?: {
  [key: string]: SimpleOption[]
}) => {
  const [getFilters, { data, loading, error, called }] =
    useGetInsightsPortalFilterOptionsLazyQuery()

  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const schema = useSchema(InsightsPortalSchema.OrderDelivery)

  const queryTags = useInsightsPortalQueryTags()

  useEffect(() => {
    const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }
    getFilters({
      variables: {
        schema,
        partnerIds,
        retailerIds,
        retailerLocationIds,
        filters: transformFilterSelectionsToOrderDeliveriesSchemaFilters(filterValues, dates),
        owner: InsightsPortalOwner.RetailerTools,
        queryTags,
      },
    })
  }, [
    filtersObjOverride,
    selectedFiltersValues,
    getFilters,
    dates,
    partnerIds,
    retailerIds,
    retailerLocationIds,
    schema,
    queryTags,
  ])

  const apiFilterOptions = transformApiOptionsIntoDashboardOptions(
    data?.insightsPortalServiceGetInsightsPortalFilterOptions?.filterOptions,
    transformOrderDeliveriesApiOptionsConfig
  )

  const dashboardFilterOptions = Object.entries(apiFilterOptions).reduce(
    (dashboardFilterOptions, [apiFilterName, filterOptions]) => {
      dashboardFilterOptions[mapApiNameToDashboardName[apiFilterName]] = filterOptions

      return dashboardFilterOptions
    },
    {} as BaseDynamicFilterOptions
  )

  return {
    filterOptions: dashboardFilterOptions,
    loading,
    error,
    called,
  }
}

type UseMultiGroupQuery = {
  metrics: OrderDeliveryMetrics[]
  groupBys: string[]
  skip?: boolean
} & Pick<GetInsightsPortalMultiGroupDataQueryVariables, 'orderBys' | 'limit' | 'offset'>

export const useMultiGroupQueryForOrderDeliveries = ({
  metrics,
  groupBys,
  limit,
  offset,
  skip,
}: UseMultiGroupQuery) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const { selectedDataExplorerValues } = useDataExplorerParamsContext()
  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')
  const currentPeriodDateRange = {
    from,
    to,
  }
  const schema = useSchema(InsightsPortalSchema.OrderDelivery)

  const { data, loading, called, error } = useGetInsightsPortalMultiGroupDataQuery({
    variables: {
      schema,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      metrics,
      groupBys,
      limit,
      offset,
      orderBys: selectedDataExplorerValues.orderBys ? [selectedDataExplorerValues.orderBys] : [],
      filters: transformFilterSelectionsToOrderDeliveriesSchemaFilters(
        selectedFiltersValues,
        currentPeriodDateRange
      ),
    },
    skip,
  })
  return {
    data,
    loading,
    called,
    error,
  }
}

const useOrderDeliveriesMetrics = (
  metrics: OrderDeliveryMetrics[],
  selectedFiltersValues: FilterParamsState['selectedFiltersValues'],
  dates: { from: Moment; to: Moment }
) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const schema = useSchema(InsightsPortalSchema.OrderDelivery)

  const { data, loading } = useGetInsightsPortalMetricsQuery({
    variables: {
      schema,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      metrics,
      filters: transformFilterSelectionsToOrderDeliveriesSchemaFilters(
        selectedFiltersValues,
        currentPeriodDateRange
      ),
    },
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalMetricValues?.metricValues || [],
    loading,
  }
}

type UseCreateCustomReportMutation = {
  reportName: string
  groupBys: string[]
  metrics: OrderDeliveryMetrics[]
}

export const useOrderDeliveriesCreateCustomReport = ({
  reportName,
  groupBys,
  metrics,
}: UseCreateCustomReportMutation) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const { selectedDataExplorerValues } = useDataExplorerParamsContext()
  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')
  const currentPeriodDateRange = {
    from,
    to,
  }
  const [createInsightsPortalCustomReportMutation, { data, loading, error, called }] =
    useCreateInsightsPortalCustomReportMutation({
      variables: {
        schema: useSchema(InsightsPortalSchema.OrderDelivery),
        partnerIds,
        retailerIds,
        retailerLocationIds,
        metrics,
        filters: transformFilterSelectionsToOrderDeliveriesSchemaFilters(
          selectedFiltersValues,
          currentPeriodDateRange
        ),
        groupBys,
        orderBys: selectedDataExplorerValues.orderBys ? [selectedDataExplorerValues.orderBys] : [],
        name: reportName,
      },
      refetchQueries: [
        {
          query: GetInsightsPortalListCustomReportsDocument,
          variables: {
            partnerIds,
            retailerIds,
            retailerLocationIds,
          },
        },
      ],
    })
  return {
    createInsightsPortalCustomReportMutation,
    data,
    loading,
    error,
    called,
  }
}

export const useOrderDeliveryMetricValueFormatter = () => {
  const { formatDecimal, formatCurrency, formatInteger } = useFormatValue()
  const formatters = [
    { metric: OrderDeliveryMetrics.SUM_GMV, formatter: formatCurrency, compact: false },
    { metric: OrderDeliveryMetrics.COUNT_ORDER, formatter: formatInteger, compact: false },
    { metric: OrderDeliveryMetrics.AVG_GMV, formatter: formatCurrency, compact: false },
    { metric: OrderDeliveryMetrics.COUNT_USER, formatter: formatInteger, compact: false },
    { metric: OrderDeliveryMetrics.AVG_RATING, formatter: formatDecimal, compact: false },
    { metric: OrderDeliveryMetrics.COUNT_FIRST_ORDER, formatter: formatInteger, compact: false },
  ]

  const formatter = (metric: OrderDeliveryMetrics, value: number) => {
    const metricFormatter = formatters.find(f => f.metric === metric)

    if (metricFormatter) {
      return metricFormatter.formatter(value, metricFormatter.compact)
    }
    return value.toLocaleString()
  }

  return {
    formatter,
  }
}

export const useCurrentPeriodOrderDeliveriesMetrics = (
  metrics: OrderDeliveryMetrics[],
  filtersObjOverride: { [key: string]: SimpleOption[] } = {}
) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  return useOrderDeliveriesMetrics(metrics, filterValues, currentPeriodDateRange)
}

export const useLastPeriodOrderDeliveriesMetrics = (
  metrics: OrderDeliveryMetrics[],
  filtersObjOverride: { [key: string]: SimpleOption[] } = {}
) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const lastPeriodDateRange = calculateComparisonPeriod(
    from,
    to,
    selectedFiltersValues.comparisonType
  )

  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  return useOrderDeliveriesMetrics(metrics, filterValues, lastPeriodDateRange)
}

export const useOrderDeliveriesGroupMetric = ({
  metric,
  groupBy,
  filtersObjOverride = null,
  skip = false,
  widget,
}: {
  metric: string
  groupBy: string
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  skip?: boolean
  widget: string
}) => {
  const { selectedFiltersValues, dates } = useFilterParamsContext()

  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')
  const currentPeriodDateRange = {
    from,
    to,
  }
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const schema = useSchema(InsightsPortalSchema.OrderDelivery)

  const queryTags = useInsightsPortalQueryTags({ widget })

  const { data, loading } = useGetInsightsPortalGroupMetricsQuery({
    skip: skip,
    variables: {
      schema,
      partnerIds,
      retailerIds,
      retailerLocationIds,
      primaryMetric: metric,
      groupBy,
      filters: transformFilterSelectionsToOrderDeliveriesSchemaFilters(
        filterValues,
        currentPeriodDateRange
      ),
      owner: InsightsPortalOwner.RetailerTools,
      queryTags,
    },
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupMetricValue?.values || [],
    loading,
  }
}

type UseCompareGroupedPeriodsForOrderDeliveries = {
  metric: string
  groupBy: string
  filtersObjOverride?: { [key: string]: SimpleOption[] }
  skip?: boolean
} & Pick<
  GetInsightsPortalGroupComparisonQueryVariables,
  'orderBy' | 'orderDirection' | 'limit' | 'offset' | 'joinBy'
>

export const useCompareGroupedPeriodsForOrderDeliveries = ({
  metric,
  groupBy,
  orderBy,
  orderDirection,
  limit,
  offset,
  joinBy,
  filtersObjOverride = null,
  skip = false,
}: UseCompareGroupedPeriodsForOrderDeliveries) => {
  const { partnerIds, retailerIds, retailerLocationIds } = useInsightsPortalAuthorizationContext()
  const { selectedFiltersValues, dates } = useFilterParamsContext()
  const filterValues = { ...selectedFiltersValues, ...filtersObjOverride }

  const from = dates.from.clone().startOf('day')
  const to = dates.to.clone().endOf('day')

  const currentPeriodDateRange = {
    from,
    to,
  }

  const lastPeriodDateRange = calculateComparisonPeriod(from, to, filterValues.comparisonType)

  const currentPeriodFilters = transformFilterSelectionsToOrderDeliveriesSchemaFilters(
    filterValues,
    currentPeriodDateRange
  )

  const lastPeriodFilters = transformFilterSelectionsToOrderDeliveriesSchemaFilters(
    filterValues,
    lastPeriodDateRange
  )

  const schema = useSchema(InsightsPortalSchema.OrderDelivery)

  const primaryDatasetInput = {
    schema,
    filters: currentPeriodFilters,
    metric,
    groupBy,
  }

  const comparisonDatasetInput = {
    schema,
    filters: lastPeriodFilters,
    metric,
    groupBy,
  }

  const variables: GetInsightsPortalGroupComparisonQueryVariables = {
    partnerIds,
    retailerIds,
    retailerLocationIds,
    primaryDataset: primaryDatasetInput,
    comparisonDataset: comparisonDatasetInput,
    joinBy: joinBy || InsightsPortalGroupComparisonRequestJoinBy.RowNumber,
    orderBy,
    orderDirection,
    limit,
    offset,
  }

  const { data, ...rest } = useGetInsightsPortalGroupComparisonQuery({
    variables,
    skip: skip,
  })

  return {
    data: data?.insightsPortalServiceGetInsightsPortalGroupComparison?.values || [],
    ...rest,
  }
}

export const useOrderDeliveriesMetricLabels = () =>
  useDomainMessages({
    SUM_GMV: 'insightsPortalDomain.performanceDashboard.metrics.SUM_GMV',
    COUNT_ORDER: 'insightsPortalDomain.performanceDashboard.metrics.COUNT_ORDER',
    AVG_GMV: 'insightsPortalDomain.performanceDashboard.metrics.AVG_GMV',
    COUNT_USER: 'insightsPortalDomain.performanceDashboard.metrics.COUNT_USER',
    AVG_RATING: 'insightsPortalDomain.performanceDashboard.metrics.AVG_RATING',
    COUNT_FIRST_ORDER: 'insightsPortalDomain.performanceDashboard.metrics.COUNT_FIRST_ORDER',
  })
